import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaExclamationTriangle, FaTimes, FaFileContract, FaGraduationCap, FaArrowRight } from 'react-icons/fa';
import useAssignmentAlerts from './useAssignmentAlerts';
import styles from './AssignmentAlerts.module.css';

// Okunmamış SOP hatırlatması oturum başına bir kez gösterilir.
const SOP_PROMPT_SESSION_KEY = 'unreadSopPromptShown';

/**
 * İki uyarıyı birlikte yönetir:
 *  1. Son tamamlama tarihine 2 gün kalan atamalar için uygulama geneli
 *     kırmızı uyarı bandı.
 *  2. Girişte bir kez çıkan "okunmamış SOP" hatırlatması.
 */
const AssignmentAlerts = () => {
  const navigate = useNavigate();
  const { dueSoon, unreadSOPs, loaded } = useAssignmentAlerts();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showSopPrompt, setShowSopPrompt] = useState(false);

  useEffect(() => {
    if (!loaded || unreadSOPs.length === 0) return;
    try {
      if (sessionStorage.getItem(SOP_PROMPT_SESSION_KEY)) return;
      sessionStorage.setItem(SOP_PROMPT_SESSION_KEY, '1');
    } catch (e) {
      // sessionStorage kapalıysa hatırlatma yine de gösterilir
    }
    setShowSopPrompt(true);
  }, [loaded, unreadSOPs.length]);

  const goToProfile = () => {
    setShowSopPrompt(false);
    setBannerDismissed(true);
    navigate('/profile');
  };

  const overdueCount = dueSoon.filter((item) => item.overdue).length;
  const showBanner = loaded && dueSoon.length > 0 && !bannerDismissed;

  return (
    <>
      {showBanner && (
        <div className={styles.banner} role="alert">
          <FaExclamationTriangle className={styles.bannerIcon} />
          <div className={styles.bannerText}>
            <strong className={styles.bannerTitle}>
              {overdueCount > 0
                ? `${overdueCount} atamanızın süresi doldu`
                : `Son 2 gün: tamamlamanız gereken ${dueSoon.length} atama var`}
            </strong>
            <span className={styles.bannerList}>
              {dueSoon.slice(0, 3).map((item) => (
                <span key={`${item.type}-${item.id}`} className={styles.bannerItem}>
                  {item.type === 'SOP' ? <FaFileContract /> : <FaGraduationCap />}
                  {item.name}
                  <span className={styles.bannerDeadline}>{item.deadlineLabel}</span>
                </span>
              ))}
              {dueSoon.length > 3 && (
                <span className={styles.bannerMore}>+{dueSoon.length - 3} tane daha</span>
              )}
            </span>
          </div>
          <button type="button" className={styles.bannerAction} onClick={goToProfile}>
            Tamamla <FaArrowRight />
          </button>
          <button
            type="button"
            className={styles.bannerClose}
            onClick={() => setBannerDismissed(true)}
            aria-label="Uyarıyı kapat"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {showSopPrompt && (
        <div className={styles.promptOverlay} onClick={() => setShowSopPrompt(false)}>
          <div className={styles.prompt} onClick={(e) => e.stopPropagation()}>
            <div className={styles.promptHeader}>
              <h2 className={styles.promptTitle}>
                <FaFileContract className={styles.promptTitleIcon} />
                Okunmamış SOP&apos;larınız var
              </h2>
              <button
                type="button"
                className={styles.promptClose}
                onClick={() => setShowSopPrompt(false)}
                aria-label="Kapat"
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.promptBody}>
              <p className={styles.promptLead}>
                Size atanmış <strong>{unreadSOPs.length}</strong> SOP dokümanını henüz
                okundu olarak işaretlemediniz. Okundu işaretledikten sonra bu uyarı bir
                daha görünmeyecek.
              </p>
              <ul className={styles.promptList}>
                {unreadSOPs.slice(0, 6).map((item) => (
                  <li
                    key={item.id}
                    className={`${styles.promptItem} ${item.dueSoon ? styles.promptItemUrgent : ''}`}
                  >
                    <span className={styles.promptItemName}>{item.name}</span>
                    {item.dueDate && (
                      <span
                        className={`${styles.promptItemDeadline} ${
                          item.dueSoon ? styles.promptItemDeadlineUrgent : ''
                        }`}
                      >
                        {item.deadlineLabel}
                      </span>
                    )}
                  </li>
                ))}
                {unreadSOPs.length > 6 && (
                  <li className={styles.promptMore}>
                    +{unreadSOPs.length - 6} SOP daha
                  </li>
                )}
              </ul>
            </div>

            <div className={styles.promptActions}>
              <button
                type="button"
                className={styles.promptSecondary}
                onClick={() => setShowSopPrompt(false)}
              >
                Daha sonra
              </button>
              <button type="button" className={styles.promptPrimary} onClick={goToProfile}>
                SOP&apos;lara git <FaArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignmentAlerts;
